import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  makeStyles,
  Spinner,
  Body1,
  Caption1,
  Badge,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  EditRegular,
  AddRegular,
  DeleteRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { dataverseClient, TABLES } from '@shared/api';
import { assertDateLiteral } from '@shared/lib';

// =============================================================================
// Types
// =============================================================================

interface AuditEntry {
  id: string;
  table: string;
  tableLabel: string;
  name: string;
  modifiedOn: string;
  modifiedByName: string;
  createdOn: string;
  createdByName: string;
  isNew: boolean;
  isDeactivated: boolean;
}

interface RawEntity {
  [key: string]: unknown;
  createdon?: string;
  modifiedon?: string;
  _createdby_value?: string;
  _modifiedby_value?: string;
  statecode?: number;
}

// =============================================================================
// Config
// =============================================================================

const CATALOG_TABLES = [
  { table: TABLES.SOLUTION, label: 'Solution', idField: 'vba_solutionid', nameField: 'vba_name' },
  { table: TABLES.SERVICE_GROUP, label: 'Service Group', idField: 'vba_servicegroupid', nameField: 'vba_name' },
  { table: TABLES.SERVICE, label: 'Service', idField: 'vba_serviceid', nameField: 'vba_name' },
  { table: TABLES.SERVICE_OPTION, label: 'Service Option', idField: 'vba_serviceoptionid', nameField: 'vba_name' },
  { table: TABLES.MANAGED_LIST, label: 'Managed List', idField: 'vba_managedlistid', nameField: 'vba_name' },
  { table: TABLES.GI_CATEGORY, label: 'GI Category', idField: 'vba_gicategoryid', nameField: 'vba_name' },
  { table: TABLES.GI_FIELD, label: 'GI Field', idField: 'vba_gifieldid', nameField: 'vba_name' },
  { table: TABLES.ADMIN_CONFIG, label: 'Config', idField: 'vba_adminconfigid', nameField: 'vba_name' },
] as const;

type TimeFilter = '24h' | '7d' | '30d';

// =============================================================================
// Data fetching
// =============================================================================

async function fetchRecentChanges(hours: number): Promise<AuditEntry[]> {
  const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
  const allEntries: AuditEntry[] = [];

  const results = await Promise.allSettled(
    CATALOG_TABLES.map(async ({ table, label, idField, nameField }) => {
      const response = await dataverseClient.list<RawEntity>(table, {
        select: [idField, nameField, 'createdon', 'modifiedon', '_createdby_value', '_modifiedby_value', 'statecode'],
        filter: `modifiedon ge ${assertDateLiteral(since, 'since')}`,
        orderBy: 'modifiedon desc',
        top: 25,
      });

      return response.value.map((entity) => {
        const createdOn = entity.createdon ?? '';
        const modifiedOn = entity.modifiedon ?? '';
        // Records created within 2 seconds of modification are "new"
        const isNew = createdOn && modifiedOn
          ? Math.abs(new Date(modifiedOn).getTime() - new Date(createdOn).getTime()) < 2000
          : false;

        return {
          id: entity[idField] as string,
          table,
          tableLabel: label,
          name: (entity[nameField] as string) ?? '(unnamed)',
          modifiedOn,
          modifiedByName: (entity['_modifiedby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? 'Unknown',
          createdOn,
          createdByName: (entity['_createdby_value@OData.Community.Display.V1.FormattedValue'] as string) ?? 'Unknown',
          isNew,
          isDeactivated: entity.statecode === 1,
        };
      });
    })
  );

  for (const result of results) {
    if (result.status === 'fulfilled') {
      allEntries.push(...result.value);
    }
  }

  // Sort all entries by modifiedOn descending
  allEntries.sort((a, b) => new Date(b.modifiedOn).getTime() - new Date(a.modifiedOn).getTime());

  return allEntries.slice(0, 100);
}

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterDropdown: {
    minWidth: '100px',
  },
  userFilter: {
    minWidth: '140px',
  },
  filterIcon: {
    fontSize: '14px',
    color: '#666',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  entry: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 10px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  iconCol: {
    paddingTop: '2px',
    flexShrink: 0,
  },
  contentCol: {
    flex: 1,
    minWidth: 0,
  },
  topLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  name: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
  },
  meta: {
    color: '#666',
    fontSize: '11px',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '24px',
    color: '#888',
  },
  createIcon: { color: '#4caf50' },
  editIcon: { color: '#1976d2' },
  deleteIcon: { color: '#d32f2f' },
});

// =============================================================================
// Helpers
// =============================================================================

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const TIME_FILTER_HOURS: Record<TimeFilter, number> = {
  '24h': 24,
  '7d': 168,
  '30d': 720,
};

// =============================================================================
// Component
// =============================================================================

const ALL_USERS = '__all__';

export function CatalogActivityLog() {
  const styles = useStyles();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('7d');
  const [userFilter, setUserFilter] = useState(ALL_USERS);

  const { data: entries, isLoading } = useQuery({
    queryKey: ['catalogActivity', timeFilter],
    queryFn: () => fetchRecentChanges(TIME_FILTER_HOURS[timeFilter]),
  });

  // Derive unique users from the data
  const uniqueUsers = useMemo(() => {
    if (!entries?.length) return [];
    const names = new Set<string>();
    for (const e of entries) {
      if (e.modifiedByName && e.modifiedByName !== 'Unknown') names.add(e.modifiedByName);
      if (e.createdByName && e.createdByName !== 'Unknown') names.add(e.createdByName);
    }
    return Array.from(names).sort();
  }, [entries]);

  // Filter entries by selected user
  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    if (userFilter === ALL_USERS) return entries;
    return entries.filter(
      (e) => e.modifiedByName === userFilter || (e.isNew && e.createdByName === userFilter),
    );
  }, [entries, userFilter]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Body1 style={{ fontWeight: 600 }}>Recent Changes</Body1>
        <div className={styles.filters}>
          <PersonRegular className={styles.filterIcon} />
          <Dropdown
            className={styles.userFilter}
            size="small"
            value={userFilter === ALL_USERS ? 'All users' : userFilter}
            selectedOptions={[userFilter]}
            onOptionSelect={(_, data) => setUserFilter(data.optionValue ?? ALL_USERS)}
          >
            <Option value={ALL_USERS}>All users</Option>
            {uniqueUsers.map((name) => (
              <Option key={name} value={name}>{name}</Option>
            ))}
          </Dropdown>
          <Dropdown
            className={styles.filterDropdown}
            size="small"
            value={timeFilter === '24h' ? 'Last 24 hours' : timeFilter === '7d' ? 'Last 7 days' : 'Last 30 days'}
            selectedOptions={[timeFilter]}
            onOptionSelect={(_, data) => setTimeFilter(data.optionValue as TimeFilter)}
          >
            <Option value="24h">Last 24 hours</Option>
            <Option value="7d">Last 7 days</Option>
            <Option value="30d">Last 30 days</Option>
          </Dropdown>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" label="Loading activity..." />
        </div>
      ) : !filteredEntries.length ? (
        <div className={styles.empty}>
          <Caption1>{userFilter !== ALL_USERS ? `No changes by ${userFilter} in this period.` : 'No changes in this time period.'}</Caption1>
        </div>
      ) : (
        <div className={styles.list}>
          {filteredEntries.map((entry) => {
            const action = entry.isDeactivated ? 'deleted' : entry.isNew ? 'created' : 'edited';
            const IconComponent = entry.isDeactivated ? DeleteRegular : entry.isNew ? AddRegular : EditRegular;
            const iconClass = entry.isDeactivated ? styles.deleteIcon : entry.isNew ? styles.createIcon : styles.editIcon;

            return (
              <div key={`${entry.table}-${entry.id}`} className={styles.entry}>
                <div className={`${styles.iconCol} ${iconClass}`}>
                  <IconComponent />
                </div>
                <div className={styles.contentCol}>
                  <div className={styles.topLine}>
                    <span className={styles.name}>{entry.name}</span>
                    <Badge size="small" appearance="outline">{entry.tableLabel}</Badge>
                  </div>
                  <Caption1 className={styles.meta}>
                    {action === 'created'
                      ? `Created by ${entry.createdByName}`
                      : `${action === 'deleted' ? 'Deleted' : 'Edited'} by ${entry.modifiedByName}`}
                    {' \u00b7 '}
                    {formatRelativeTime(entry.modifiedOn)}
                  </Caption1>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
